import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axiosClient from '../services/setupInterceptors';

const CART_STORAGE_KEY = 'guestCart';

const getInitialItems = () => {
  try {
    const serializedCart = localStorage.getItem(CART_STORAGE_KEY);
    return serializedCart ? JSON.parse(serializedCart) : [];
  } catch (e) {
    console.error("Could not load cart from localStorage", e);
    return [];
  }
};

const updateLocalStorage = (items) => {
  localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
};

const initialState = {
  items: getInitialItems(),
  total: 0,
  final_total: 0,
  shipping_fee: 0,
  itemCount: 0,
  status: 'idle',
  error: null,
  offer_discount : 0
};

const cartApiThunk = async (url, method, body, { rejectWithValue }) => {
  try {
    const response = await axiosClient({ url, method, data: body });
    return response.data.data;
  } catch (err) {
    return rejectWithValue(err.response?.data || err.message);
  }
};




export const fetchCartFromServer = createAsyncThunk(
  'cart/fetchFromServer',
  async (_, { rejectWithValue }) => {
    return cartApiThunk('/cart/', 'GET', null, { rejectWithValue });
  }
);

export const mergeLocalCartOnLogin = createAsyncThunk(
  'cart/mergeOnLogin',
  async (_, { getState, rejectWithValue }) => {
    const localItems = getState().cart.items.map(item => ({
      product_id: item.product?.id,
      variant_id: item.variant?.id,
      quantity: item.quantity,
    }));

    return cartApiThunk('/cart/merge', 'POST', localItems, { rejectWithValue });
  }
);

export const addItemToCartServer = createAsyncThunk(
  'cart/addItemToCartServer',
  async (payload, { rejectWithValue }) => {
    return cartApiThunk('/cart/', 'POST', payload, { rejectWithValue });
  }
);

export const updateItemQuantityServer = createAsyncThunk(
  'cart/updateItemQuantityServer',
  async (payload, { rejectWithValue }) => {
    return cartApiThunk(
      `/cart/${payload.variant_id}`,
      'PATCH',
      { quantity: payload.quantity },
      { rejectWithValue }
    );
  }
);

export const removeItemFromServer = createAsyncThunk(
  'cart/removeItemFromServer',
  async (payload, { rejectWithValue }) => {
    return cartApiThunk(`/cart/${payload.variant_id}`, 'DELETE', null, { rejectWithValue });
  }
);




const cartSlice = createSlice({
  name: 'cart',
  initialState,

  reducers: {
    updateLocalCart(state, { payload }) {
      const { action, itemPayload } = payload;
      const { product, variant, quantity } = itemPayload;

      let newItems = [...state.items];
      const index = newItems.findIndex(
        i => i.product.id === product.id && i.variant.id === variant.id
      );

      if (action === "ADD") {
        if (index >= 0) newItems[index].quantity += quantity;
        else newItems.push({ product, variant, quantity });

      } else if (action === "SET_QUANTITY") {
        if (index >= 0) {
          newItems[index].quantity = quantity;
          if (quantity <= 0) newItems.splice(index, 1);
        }

      } else if (action === "REMOVE") {
        if (index >= 0) newItems.splice(index, 1);
      }

      state.items = newItems;
      state.total = newItems.reduce((sum, i) => sum + i.variant.display_price * i.quantity, 0);
      state.final_total = state.total;
      state.itemCount = newItems.reduce((sum, i) => sum + i.quantity, 0);
      

      updateLocalStorage(newItems);
    },

    clearLocalCartData(state) {
      state.items = [];
      state.total = 0;
      state.final_total = 0;
      state.shipping_fee = 0;
      state.itemCount = 0;
      state.offer_discount = 0;
      localStorage.removeItem(CART_STORAGE_KEY);
      localStorage.removeItem("cart");
    }
  },

  extraReducers: (builder) => {
    const handlePending = (state) => {
      state.status = 'loading';
      state.error = null;
    };

    const handleRejected = (state, { payload }) => {
      state.status = 'failed';
      state.error = payload;
    };

    const handleServerFulfilled = (state, { payload }) => {
      state.status = 'idle';
      state.error = null;

    
      if (payload && payload.items) {
        state.items = payload.items;
        state.total = payload.total ?? 0;
        state.final_total = payload.final_total ?? 0;
        state.shipping_fee = payload.shipping_fee ?? 0;
        state.offer_discount = payload.offer_discount ?? 0;

        state.itemCount = payload.items.reduce(
          (sum, item) => sum + item.quantity,
          0
        );

        if (payload.status === "SUCCESS") {
          localStorage.removeItem(CART_STORAGE_KEY);
        }
      }
    };

    builder
      .addCase(fetchCartFromServer.pending, handlePending)
      .addCase(fetchCartFromServer.fulfilled, handleServerFulfilled)
      .addCase(fetchCartFromServer.rejected, handleRejected)

      .addCase(mergeLocalCartOnLogin.pending, handlePending)
      .addCase(mergeLocalCartOnLogin.fulfilled, handleServerFulfilled)
      .addCase(mergeLocalCartOnLogin.rejected, handleRejected)

      .addCase(addItemToCartServer.pending, handlePending)
      .addCase(addItemToCartServer.fulfilled, handleServerFulfilled)
      .addCase(addItemToCartServer.rejected, handleRejected)

      .addCase(updateItemQuantityServer.pending, handlePending)
      .addCase(updateItemQuantityServer.fulfilled, handleServerFulfilled)
      .addCase(updateItemQuantityServer.rejected, handleRejected)

      .addCase(removeItemFromServer.pending, handlePending)
      .addCase(removeItemFromServer.fulfilled, handleServerFulfilled)
      .addCase(removeItemFromServer.rejected, handleRejected)
  }
});


export const { updateLocalCart, clearLocalCartData } = cartSlice.actions;

export default cartSlice.reducer;
